import { type Invitation } from '@/organization/useOrganizations';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import WorkIcon from '@mui/icons-material/Work';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  invitation: Invitation;
}

export function InvitationDetailsDialog({ open, onClose, invitation }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(invitation.signupUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getStatus = () => {
    if (invitation.usedAt) {
      return { label: 'Used', color: 'success' as const };
    }
    if (invitation.isExpired) {
      return { label: 'Expired', color: 'error' as const };
    }
    return { label: 'Pending', color: 'primary' as const };
  };

  const status = getStatus();

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Typography variant='h6'>Invitation Details</Typography>
          <Chip label={status.label} color={status.color} size='small' />
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Recipient Info */}
          <Box>
            <Stack direction='row' alignItems='center' spacing={1} mb={1}>
              <EmailIcon fontSize='small' sx={{ color: 'text.secondary' }} />
              <Typography variant='subtitle2' color='text.secondary'>
                Recipient
              </Typography>
            </Stack>
            <Typography variant='body1'>{invitation.email}</Typography>
          </Box>
          <Box>
            <Stack direction='row' alignItems='center' spacing={1} mb={1}>
              <WorkIcon fontSize='small' sx={{ color: 'text.secondary' }} />
              <Typography variant='subtitle2' color='text.secondary'>
                Assigned Role
              </Typography>
            </Stack>
            <Typography variant='body1'>{invitation.role}</Typography>
          </Box>
          {/* Sender Info */}
          <Box>
            <Stack direction='row' alignItems='center' spacing={1} mb={1}>
              <PersonIcon fontSize='small' sx={{ color: 'text.secondary' }} />
              <Typography variant='subtitle2' color='text.secondary'>
                Sent By
              </Typography>
            </Stack>
            <Typography variant='body1'>
              {invitation.createdByName} on{' '}
              {format(new Date(invitation.createdAt), 'MMM d, yyyy h:mm a')}
            </Typography>
          </Box>

          {/* Used Info */}
          {invitation.usedAt && invitation.usedByName && (
            <Box>
              <Stack direction='row' alignItems='center' spacing={1} mb={1}>
                <CalendarTodayIcon
                  fontSize='small'
                  sx={{ color: 'text.secondary' }}
                />
                <Typography variant='subtitle2' color='text.secondary'>
                  Account Created
                </Typography>
              </Stack>
              <Typography variant='body1'>
                By {invitation.usedByName} on{' '}
                {format(new Date(invitation.usedAt), 'MMM d, yyyy h:mm a')}
              </Typography>
            </Box>
          )}

          <Divider />

          {/* QR Code and Link */}
          {invitation.isValid ? (
            <Stack spacing={3}>
              {/* Invitation Link */}
              <Box>
                <Stack direction='row' alignItems='center' spacing={1} mb={1}>
                  <LinkIcon fontSize='small' sx={{ color: 'text.secondary' }} />
                  <Typography variant='subtitle2' color='text.secondary'>
                    Invitation Link
                  </Typography>
                </Stack>
                <TextField
                  fullWidth
                  value={invitation.signupUrl}
                  InputProps={{
                    readOnly: true,
                    sx: { fontSize: '0.875rem' },
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={handleCopy}
                          edge='end'
                          size='small'
                          color={copied ? 'success' : 'default'}
                        >
                          {copied ? (
                            <CheckIcon fontSize='small' />
                          ) : (
                            <ContentCopyIcon fontSize='small' />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  size='small'
                />
              </Box>

              {/* QR Code */}
              <Stack alignItems='center' spacing={2}>
                <Stack direction='row' alignItems='center' spacing={1}>
                  <QrCode2Icon
                    fontSize='small'
                    sx={{ color: 'text.secondary' }}
                  />
                  <Typography variant='subtitle2' color='text.secondary'>
                    QR Code
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'white',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <QRCodeSVG
                    value={invitation.signupUrl}
                    size={150}
                    level='M'
                    includeMargin={false}
                  />
                </Box>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  textAlign='center'
                >
                  Scan to open signup page
                </Typography>
              </Stack>

              <Typography
                variant='caption'
                color='text.secondary'
                textAlign='center'
              >
                Share this link or QR code with the recipient to allow them to
                create their account.
              </Typography>
            </Stack>
          ) : (
            <Alert severity={invitation.usedAt ? 'info' : 'warning'}>
              {invitation.usedAt
                ? 'This invitation has already been used.'
                : 'This invitation has expired and can no longer be used.'}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
